// Stage Mail-V2.5 (2026-08-20): MailV2SyncOverlay.
//
// Mail V2.5 locked plan §8 + §16.9.5 + §16.9.6:
// - overlay covers ONLY `mv2-body` div (position: absolute filling the parent)
// - `stale` is INTERMEDIATE (not terminal): keep polling, show "中断" card
//   WITHOUT close button. Backend sweep-stale may recover stale → pending →
//   running automatically; overlay observes the transition naturally.
// - `completed` / `failed` are terminal: fire callback once, show card with
//   close button. Close only clears local terminalVisible; parent owns the
//   activeSyncJobId state (per §16.9.6 rule 4: "只因为用户关闭弹窗 UI 就
//   当作 job 已结束" is forbidden).
import React from "react";
import { useSyncJob } from "../hooks/useSyncJob";

export type MailV2SyncOverlayProps = {
  jobId: string | null;
  onCompleted?: () => void;
  onFailed?: (errorMessage: string | null) => void;
  onStale?: () => void;
  onCancel?: (jobId: string) => void | Promise<void>;
};

const RUNNING_HINTS = [
  "最新のメール内容を更新しています。",
  "あと少しで完了します。",
  "このまま待つか、ほかの画面を先にご確認ください。",
] as const;

export function MailV2SyncOverlay(props: MailV2SyncOverlayProps): React.ReactElement | null {
  const { jobId, onCompleted, onFailed, onStale, onCancel } = props;
  const { job, error } = useSyncJob(jobId);
  const [terminalVisible, setTerminalVisible] = React.useState(false);
  const [hintIndex, setHintIndex] = React.useState(0);
  const [cancelBusy, setCancelBusy] = React.useState(false);
  const status = job?.status ?? "pending";

  // Fire terminal callbacks once (completed / failed). stale does NOT fire
  // a callback — it is intermediate; the overlay keeps observing via
  // useSyncJob's 3s polling.
  const firedRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!job) return;
    const key = `${job.id}:${job.status}`;
    if (firedRef.current === key) return;
    if (job.status === "completed") {
      firedRef.current = key;
      setTerminalVisible(true);
      onCompleted?.();
    } else if (job.status === "failed") {
      firedRef.current = key;
      setTerminalVisible(true);
      onFailed?.(job.error_message);
    } else if (job.status === "stale") {
      firedRef.current = key;
      onStale?.();
      // DO NOT setTerminalVisible for stale — it's intermediate.
    }
  }, [job, onCompleted, onFailed, onStale]);

  React.useEffect(() => {
    if (!jobId) {
      setTerminalVisible(false);
    }
  }, [jobId]);

  React.useEffect(() => {
    if (!jobId || (status !== "pending" && status !== "running")) {
      setHintIndex(0);
      return;
    }
    const timer = window.setInterval(() => {
      setHintIndex((current) => (current + 1) % RUNNING_HINTS.length);
    }, 1000);
    return () => window.clearInterval(timer);
  }, [jobId, job?.status]);

  const handleCancel = React.useCallback(async () => {
    if (!jobId || !onCancel || cancelBusy) return;
    try {
      setCancelBusy(true);
      await onCancel(jobId);
    } finally {
      setCancelBusy(false);
    }
  }, [cancelBusy, jobId, onCancel]);

  if (!jobId) return null;

  const totalTarget = job?.total_target_count ?? 0;
  const processed = job?.processed_count ?? 0;
  const failed = job?.failed_count ?? 0;
  const activeHint = RUNNING_HINTS[hintIndex];
  const shouldShowProgress = totalTarget > 0 || processed > 0;
  const runningMailbox =
    job?.progress?.find((row) => row.status === "running") ??
    job?.progress?.find((row) => row.status === "pending") ??
    null;
  const currentMailbox = runningMailbox?.mailbox_name ?? null;
  const currentMailboxProcessed = runningMailbox?.processed_count ?? 0;
  const currentMailboxTarget = runningMailbox?.target_count ?? 0;
  const currentMailboxProgressLabel =
    currentMailboxTarget > 0
      ? `${currentMailboxProcessed} / ${currentMailboxTarget}`
      : `${currentMailboxProcessed}`;

  // stale intermediate state (§16.9.5 + §16.9.6 rule 3): overlay stays
  // mounted, no close button. Polling continues (handled by useSyncJob
  // because it only stops on completed/failed).
  if (status === "stale") {
    return (
      <div className="mv2-sync-overlay" role="dialog" aria-busy="true" aria-modal="true">
        <div className="mv2-sync-overlay__card mv2-sync-overlay__card--stale">
          <div aria-hidden="true" className="mv2-sync-overlay__spinner mv2-sync-overlay__spinner--muted" />
          <div className="mv2-sync-overlay__title">同期が中断しました</div>
          <div className="mv2-sync-overlay__hint">
            サーバ側の sweeper が自動回復を試みます。完了までそのままお待ちください。
          </div>
          <div className="mv2-sync-overlay__hint mv2-sync-overlay__hint--muted">
            または [設定] から修復ジョブを実行できます。
          </div>
          {job?.error_message ? (
            <div className="mv2-sync-overlay__error">{job.error_message}</div>
          ) : null}
          {error ? (
            <div className="mv2-sync-overlay__error">{error}</div>
          ) : null}
        </div>
      </div>
    );
  }

  if (status === "completed" && terminalVisible) {
    return (
      <div className="mv2-sync-overlay" role="dialog" aria-modal="true" aria-hidden="false">
        <div className="mv2-sync-overlay__card mv2-sync-overlay__card--done">
          <div className="mv2-sync-overlay__title">同期完了</div>
          <div className="mv2-sync-overlay__count">{processed} 件</div>
          <button
            className="mv2-sync-overlay__btn"
            onClick={() => setTerminalVisible(false)}
          >
            閉じる
          </button>
        </div>
      </div>
    );
  }

  if (status === "failed" && terminalVisible) {
    return (
      <div className="mv2-sync-overlay" role="dialog" aria-modal="true" aria-hidden="false">
        <div className="mv2-sync-overlay__card mv2-sync-overlay__card--err">
          <div className="mv2-sync-overlay__title" style={{ color: "#dc2626" }}>
            同期に失敗しました
          </div>
          {job?.error_message ? (
            <div className="mv2-sync-overlay__error">{job.error_message}</div>
          ) : null}
          <div className="mv2-sync-overlay__hint">
            設定画面から修復を実行してください。
          </div>
          <button
            className="mv2-sync-overlay__btn"
            onClick={() => setTerminalVisible(false)}
          >
            閉じる
          </button>
        </div>
      </div>
    );
  }

  // pending / running
  const progressLabel =
    totalTarget > 0 ? `${processed} / ${totalTarget}` : `${processed}`;

  return (
    <div className="mv2-sync-overlay" role="dialog" aria-busy="true" aria-modal="true">
      <div className="mv2-sync-overlay__card">
        <div aria-hidden="true" className="mv2-sync-overlay__spinner" />
        <div className="mv2-sync-overlay__title">メールを同期しています</div>
        <div className="mv2-sync-overlay__message" aria-live="polite">{activeHint}</div>
        {shouldShowProgress ? (
          <div className="mv2-sync-overlay__count">{progressLabel}</div>
        ) : null}
        {currentMailbox ? (
          <div className="mv2-sync-overlay__hint">
            現在のメールボックス: {currentMailbox}
          </div>
        ) : null}
        {currentMailbox ? (
          <div className="mv2-sync-overlay__hint mv2-sync-overlay__hint--muted">
            この箱の進捗: {currentMailboxProgressLabel}
          </div>
        ) : null}
        <div className="mv2-sync-overlay__hint mv2-sync-overlay__hint--muted">
          50件ずつ保存しながら、新しいメールから順に同期しています。
        </div>
        {failed > 0 ? (
          <div className="mv2-sync-overlay__warn">失敗 {failed} 件</div>
        ) : null}
        {error ? (
          <div className="mv2-sync-overlay__error">{error}</div>
        ) : null}
        {onCancel ? (
          <button
            className="mv2-sync-overlay__btn"
            onClick={() => {
              void handleCancel();
            }}
            disabled={cancelBusy}
          >
            {cancelBusy ? "停止中..." : "同期を停止"}
          </button>
        ) : null}
      </div>
    </div>
  );
}
